import React, { useRef, useState } from 'react';
import { ClipboardPaste, Copy, Download, FileUp, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const STORY_PROMPT = `You are helping a person create an interactive story for the TweePorter.ca Free Preview Workspace.

First, ask the user these questions before writing any story code:
1. What type of story would you like to create?
2. Is it for personal enjoyment, a business, training, education, or another purpose?
3. What would make it fun, engaging, important, or useful for your audience?
4. Who is the intended audience, and what choice or outcome should they experience?

After the user answers, create one concise, engaging story that follows every requirement below:
- Use valid Twee 3 / simple SugarCube-style syntax only.
- Start with :: StoryTitle and :: Start passages.
- Use links exactly like [[Choice label|TargetPassage]]. Every target must have a matching :: Passage Name.
- Keep the story to 12 reachable passages or fewer, including Start, because the Free Preview Workspace can only explore the first 12 reachable passages.
- Do not use JavaScript, <script> tags, SugarCube macros, variables, images, external assets, or custom CSS.
- Keep each passage brief and write at least two meaningful choices where appropriate.
- Include a clear ending and let the reader restart or return to a useful earlier decision.

When the story is ready, return only the complete Twee code in one fenced code block. Do not include explanations before or after the code.`;

const downloadPrompt = () => {
  const url = URL.createObjectURL(new Blob([STORY_PROMPT], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'tweeporter-free-preview-ai-prompt.txt';
  link.click();
  URL.revokeObjectURL(url);
};

const AIStoryGuidance = ({ onCopied, onPasteFromClipboard, onLoadTextFile }) => {
  const [copied, setCopied] = useState(false);
  const textFileInputRef = useRef(null);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(STORY_PROMPT);
      setCopied(true);
      onCopied?.('AI guidance copied. Paste it into your preferred AI, answer its questions, then paste the Twee code here.');
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      onCopied?.('Clipboard access is unavailable. Select and copy the guidance shown below.');
    }
  };

  const loadTextFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const content = await file.text();
      if (!content.trim()) throw new Error('empty file');
      onLoadTextFile?.(content);
    } catch {
      onCopied?.('That file is empty or could not be read. Choose a text file containing your completed Twee code.');
    }
    event.target.value = '';
  };

  return <div className="border-t border-amber-200 pt-5" data-testid="workspace-ai-guidance-panel">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-amber-300 bg-amber-50"><Sparkles className="h-4 w-4 text-amber-700" /></span>
        <div><p className="font-semibold text-slate-900" data-testid="workspace-ai-guidance-title">Create with your own AI</p><p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600" data-testid="workspace-ai-guidance-description">Your AI will first learn what you want to make, then produce a story that fits the Free Preview limits. TweePorter never sends your story or prompt to an AI service.</p></div>
      </div>
      <Button type="button" variant="outline" onClick={downloadPrompt} data-testid="workspace-download-ai-guidance-button"><Download className="mr-2 h-4 w-4" />Download .txt</Button>
    </div>
    <pre className="mt-4 max-h-80 overflow-y-auto whitespace-pre-wrap border border-slate-700 bg-slate-950 p-4 font-mono text-xs leading-6 text-slate-100" data-testid="workspace-ai-guidance-code">{STORY_PROMPT}</pre>
    <div className="mt-4 border-l-4 border-amber-400 bg-amber-50 px-4 py-3" data-testid="workspace-ai-next-step-instructions"><p className="text-sm font-semibold text-slate-900" data-testid="workspace-ai-next-step-title">When your AI work is complete</p><p className="mt-1 text-sm leading-relaxed text-slate-700" data-testid="workspace-ai-next-step-description">Copy only the completed Twee code from your AI tool, then choose <strong>Paste code from clipboard</strong>. Or save the generated Twee code as a <code>.txt</code> or <code>.twee</code> file and choose <strong>Load code file</strong>. Either option puts the code into Step 2 for review.</p></div>
    <div className="mt-4 flex flex-wrap gap-2">
      <Button type="button" variant="outline" onClick={copyPrompt} data-testid="workspace-copy-ai-guidance-button" className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"><Copy className="mr-2 h-4 w-4" />{copied ? 'Copied prompt' : 'Copy prompt'}</Button>
      <Button type="button" variant="outline" onClick={onPasteFromClipboard} data-testid="workspace-paste-ai-result-button"><ClipboardPaste className="mr-2 h-4 w-4" />Paste code from clipboard</Button>
      <Button type="button" variant="outline" onClick={() => textFileInputRef.current?.click()} data-testid="workspace-load-ai-code-file-button"><FileUp className="mr-2 h-4 w-4" />Load code file</Button>
      <input ref={textFileInputRef} type="file" accept=".txt,.twee,text/plain" className="hidden" onChange={loadTextFile} data-testid="workspace-load-ai-code-file-input" />
    </div>
  </div>;
};

export default AIStoryGuidance;